import { z } from 'zod';
import { FAIL_REASONS, ORDER_STATUSES, PAYMENT_METHODS, PAYMENT_STATUSES, USER_ROLES } from '../enums.js';
import { dateOnly, inlineAddress, money, timeOnly } from './common.js';
/**
 * Заказ.
 *
 * СУММА НЕ ВХОДИТ В КОНТРАКТ ЗАПРОСА. Клиент присылает состав, адрес и слот;
 * итог считает сервер по ценам из базы. Сверять присланную сумму тоже не нужно:
 * расхождение из-за устаревшего прайса в кэше клиента ломало бы заказ на ровном
 * месте. См. docs/04-api.md.
 */
export const orderItem = z.object({
    product_id: z.number().int().positive(),
    qty: z.number().int().min(1).max(50),
});
export const createOrderBody = z
    .object({
    /**
     * Исполнитель, у которого оформляется заказ. Обязателен: товар, цена
     * доставки и слоты принадлежат конкретной витрине, и «заказ вообще»
     * в маркетплейсе не существует. Корзина на клиенте привязана к тому же id.
     */
    vendor_id: z.number().int().positive(),
    items: z.array(orderItem).min(1).max(30),
    address_id: z.number().int().positive().optional(),
    address: inlineAddress.optional(),
    slot_date: dateOnly,
    slot_start: timeOnly,
    payment_method: z.enum(PAYMENT_METHODS),
    comment: z.string().max(100).optional(),
})
    .refine((v) => Boolean(v.address_id) !== Boolean(v.address), {
    message: 'Укажите address_id или address, но не оба сразу',
    path: ['address_id'],
});
export const orderItemView = z.object({
    id: z.number().int().positive(),
    product_id: z.number().int().positive().nullable(),
    /** Снимок названия на момент заказа: правка каталога не меняет историю. */
    name: z.string(),
    unit_price: money,
    qty: z.number().int(),
    line_total: money,
});
export const orderStatusEvent = z.object({
    from_status: z.enum(ORDER_STATUSES).nullable(),
    to_status: z.enum(ORDER_STATUSES),
    actor_role: z.enum(USER_ROLES).nullable(),
    comment: z.string().nullable(),
    created_at: z.string(),
});
export const order = z.object({
    id: z.number().int().positive(),
    public_number: z.string(),
    status: z.enum(ORDER_STATUSES),
    payment_method: z.enum(PAYMENT_METHODS),
    payment_status: z.enum(PAYMENT_STATUSES),
    contact_name: z.string(),
    contact_phone: z.string(),
    address_text: z.string(),
    address_house: z.string().nullable(),
    address_entrance: z.string().nullable(),
    address_floor: z.string().nullable(),
    address_apartment: z.string().nullable(),
    address_landmark: z.string().nullable(),
    lat: z.number(),
    lon: z.number(),
    slot_date: dateOnly,
    slot_start: timeOnly,
    slot_end: timeOnly,
    items_total: money,
    delivery_price: money,
    total: money,
    collected_amount: money.nullable(),
    comment: z.string().nullable(),
    cancel_reason: z.string().nullable(),
    fail_reason: z.string().nullable(),
    created_at: z.string(),
    items: z.array(orderItemView),
    /** Действия, доступные текущему пользователю. Удобство UI, не защита. */
    can_cancel: z.boolean(),
});
export const orderWithHistory = order.extend({
    history: z.array(orderStatusEvent),
    courier_name: z.string().nullable(),
    courier_phone: z.string().nullable(),
});
export const ordersQuery = z.object({
    scope: z.enum(['active', 'completed', 'all']).default('active'),
});
export const cancelOrderBody = z.object({
    reason: z.string().max(200).optional(),
});
export const changeStatusBody = z.object({
    to_status: z.enum(ORDER_STATUSES),
    comment: z.string().max(200).optional(),
});
export const deliverOrderBody = z.object({
    collected_amount: money,
});
export const failOrderBody = z.object({
    reason: z.enum(FAIL_REASONS),
    comment: z.string().max(200).optional(),
});
//# sourceMappingURL=order.js.map